/**
 * CommandMenu - Global Ctrl+K search for pages and modules.
 *
 * Scoped to the current study context:
 * - If inside a study, shows that study's pages + modules only
 * - Global pages (Dashboard, Admin, etc.) always visible
 * - Never shows content from other studies
 */

import { useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { create } from 'zustand'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Activity,
  Bell,
  LayoutDashboard,
  FolderOpen,
  Database,
  ShieldAlert,
  TrendingUp,
  Users,
  AlertTriangle,
  AlertCircle,
  GitCompare,
  GitBranch,
  Settings,
  Shield,
  FolderCog,
  FileText,
  Cog,
  Blocks,
  Upload,
  Lock,
  List,
  User,
  StickyNote,
  MessageSquare,
  Tags,
  BookOpen,
  Palette,
  Workflow,
  Package,
  BarChart3,
  Table,
  PieChart,
  LineChart,
  type LucideIcon,
} from 'lucide-react'
import {
  useNavigationStore,
  defaultNavigation,
  NavigationItem,
} from '@/stores/navigationStore'
import { useStudyStore } from '@/stores/studyStore'
import { useAuthContext } from '@/hooks/AuthContext'
import { isOnStudySubdomain, navigateToMainApp } from '@/lib/subdomain'

/** Shared state so the Header search button can open the command menu. */
interface CommandMenuState {
  isOpen: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
}

export const useCommandMenuStore = create<CommandMenuState>()((set) => ({
  isOpen: false,
  setOpen: (open) => set({ isOpen: open }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
}))

const iconMap: Record<string, LucideIcon> = {
  Activity,
  Bell,
  LayoutDashboard,
  FolderOpen,
  Database,
  ShieldAlert,
  TrendingUp,
  Users,
  AlertTriangle,
  AlertCircle,
  GitCompare,
  GitBranch,
  Settings,
  Shield,
  FolderCog,
  FileText,
  Cog,
  Blocks,
  Upload,
  Lock,
  List,
  User,
  StickyNote,
  MessageSquare,
  Tags,
  BookOpen,
  Palette,
  Workflow,
  Package,
}

// Map module types to icons
const moduleTypeIconMap: Record<string, LucideIcon> = {
  cards: LayoutDashboard,
  line_charts: LineChart,
  bar_charts: BarChart3,
  pie_charts: PieChart,
  tables: Table,
  dt_listings: Table,
  specialized: Activity,
  layout: Blocks,
  issue_cards: AlertTriangle,
  issue_tables: AlertTriangle,
}

interface FlatItem {
  id: string
  label: string
  group: string
  icon?: LucideIcon
  path: string
  keywords?: string
}

interface FlattenOptions {
  group: string
  studyId: string | null
  onSubdomain: boolean
  hasRole: (role: string) => boolean
  isPageAccessible: (slug: string) => boolean
  hasPageRestrictions: boolean
  /** Skip page access checks (for addon features like Issues/Patient Profile) */
  skipAccessCheck?: boolean
}

/**
 * Flatten a NavigationItem tree into a list of searchable items.
 * Mirrors the sidebar's access control logic:
 * - requiredRoles checked on every item
 * - isPageAccessible checked on leaf items (except admin routes, addon features, study-specific pages)
 */
function flattenNavItems(
  items: NavigationItem[],
  opts: FlattenOptions,
  parentLabel?: string,
): FlatItem[] {
  const result: FlatItem[] = []

  for (const item of items) {
    // Check role-based access (same as sidebar line 127)
    if (item.requiredRoles && !item.requiredRoles.some((role) => opts.hasRole(role))) {
      continue
    }

    const Icon = item.icon ? iconMap[item.icon] : undefined
    const label = parentLabel ? `${parentLabel} > ${item.label}` : item.label

    if (item.children?.length) {
      result.push(
        ...flattenNavItems(item.children, opts, label)
      )
    } else if (item.path) {
      // Check page-level access (mirrors sidebar lines 139-147)
      const isAdminRoute = item.path.startsWith('/admin')
      const isStudySpecificPage = item.id.startsWith('study-')
      const isAddon = opts.skipAccessCheck || !!item.requiredFeature

      if (opts.hasPageRestrictions && !isAdminRoute && !isAddon && !isStudySpecificPage) {
        const pathParts = item.path.split('/').filter(Boolean)
        const slug = pathParts[pathParts.length - 1]
        if (slug && slug !== ':studyId' && !opts.isPageAccessible(slug)) {
          continue
        }
      }

      let actualPath = item.path
      if (opts.onSubdomain) {
        actualPath = item.path.replace(/^\/study\/:studyId/, '') || '/'
      } else if (opts.studyId) {
        actualPath = item.path.replace(':studyId', opts.studyId)
      }

      result.push({
        id: item.id,
        label,
        group: opts.group,
        icon: Icon,
        path: actualPath,
        keywords: item.moduleKey,
      })
    }
  }

  return result
}

export function CommandMenu() {
  const { isOpen: open, setOpen, toggle } = useCommandMenuStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasRole } = useAuthContext()

  const currentStudy = useStudyStore((state) => state.currentStudy)
  const { studyNavigation, studyModules, isPageAccessible, hasPageRestrictions } = useNavigationStore()
  const onSubdomain = isOnStudySubdomain()
  const isOnStudyRoute = location.pathname.startsWith('/study/')
  const inStudyContext = onSubdomain || isOnStudyRoute

  // Listen for Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggle()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const studyId = currentStudy?.studyId || null

  // Build flat searchable items
  const items = useMemo(() => {
    const allItems: FlatItem[] = []

    const baseOpts = { studyId, onSubdomain, hasRole, isPageAccessible, hasPageRestrictions }

    // --- Global pages (only on main domain, not in study context) ---
    if (!inStudyContext) {
      const globalItems = defaultNavigation.filter((item) => !item.requiresStudy)
      allItems.push(...flattenNavItems(globalItems, { ...baseOpts, group: 'Pages' }))
    }

    // --- Study pages (only if in a study context) ---
    // Backend already filters out pages requiring disabled features.
    if (inStudyContext && studyId) {
      if (studyNavigation.length > 0) {
        allItems.push(
          ...flattenNavItems(studyNavigation, { ...baseOpts, group: 'Study Pages' })
        )
      }

      // --- Modules (only for current study) ---
      if (studyModules.length > 0) {
        const enabledModules = studyModules.filter((m) => m.isEnabled)
        for (const mod of enabledModules) {
          const TypeIcon = moduleTypeIconMap[mod.moduleType] || Blocks
          let modulePath: string | undefined

          // Navigate to the page where the module is placed
          if (mod.pagePlacement) {
            modulePath = onSubdomain
              ? `/${mod.pagePlacement}`
              : `/study/${studyId}/${mod.pagePlacement}`
          }

          if (modulePath) {
            allItems.push({
              id: `module-${mod.id}`,
              label: mod.displayName,
              group: 'Modules',
              icon: mod.icon ? iconMap[mod.icon] || TypeIcon : TypeIcon,
              path: modulePath,
              keywords: `${mod.moduleKey} ${mod.moduleType}`,
            })
          }
        }
      }
    }

    return allItems
  }, [
    studyId,
    studyNavigation,
    studyModules,
    inStudyContext,
    onSubdomain,
    hasRole,
    isPageAccessible,
    hasPageRestrictions,
  ])

  // Group items by their group label
  const grouped = useMemo(() => {
    const groups: Record<string, FlatItem[]> = {}
    for (const item of items) {
      if (!groups[item.group]) {
        groups[item.group] = []
      }
      groups[item.group].push(item)
    }
    return groups
  }, [items])

  const handleSelect = useCallback(
    (item: FlatItem) => {
      setOpen(false)
      // Admin routes on subdomain should go to main app
      if (onSubdomain && item.path.startsWith('/admin')) {
        navigateToMainApp(item.path)
      } else {
        navigate(item.path)
      }
    },
    [navigate, onSubdomain]
  )

  const groupNames = Object.keys(grouped)

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages and modules..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {groupNames.map((groupName, idx) => (
          <div key={groupName}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={groupName}>
              {grouped[groupName].map((item) => {
                const Icon = item.icon
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.keywords || ''}`}
                    onSelect={() => handleSelect(item)}
                  >
                    {Icon && <Icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    <span>{item.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
